import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { TableSortLabel, Box, Typography, Tooltip, MenuItem } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import { Page, Row } from '../../util/Page';
import TextDataCell from './TextDataCell';
import LinearDataCell from './LinearDataCell';
import ButtonDataCell from './ButtonDataCell';
import IdentityDataCell from './IdentityDataCell';
import EquipmentIdEditor from './dialogs/EquipmentIdEditor';
import EquipmentTypeEditor from './dialogs/EquipmentTypeEditor';
import PointTypeEditor from './dialogs/PointTypeEditor';
import UnitTypeEditor from './dialogs/UnitTypeEditor';
import MultiEditMenu from './MultiEditMenu';
import BadgesDataCell from './BadgesDataCell';
import { SET_PAGE } from '../../../redux/actions/actiontypes';

const editorMap = {
  equip_type: EquipmentTypeEditor,
  point_type: PointTypeEditor,
  units: UnitTypeEditor,
  equip_id: EquipmentIdEditor,
};

const StagingDataTable = ({
  title,
  columns,
  actions,
  data,
  rowIdKey,
  numSelected,
  children,
  selected,
  onItemSelected,
  loading,
  buildingId,
  page,
  handleChangePage,
}) => {
  const rowCount = data.length;
  const [sortColumns, setSortColumns] = React.useState({});
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [currentlyEditing, setCurrentlyEditing] = React.useState([]);
  const [currentEditor, setCurrentEditor] = React.useState({});

  const renderEditorDialog = () => {
    const EditorComponent = editorMap[currentEditor.id];

    return EditorComponent ? (
      <EditorComponent
        open
        editing={currentlyEditing}
        column={currentEditor}
        actions={actions}
        setCurrentEditor={setCurrentEditor}
        setCurrentlyEditing={setCurrentlyEditing}
        loading={loading}
        buildingId={buildingId}
      />
    ) : null;
  };

  // Temporary sorting code, will be moved to backend
  const sortedData = useMemo(() => {
    let result = data;
    if (columns.map((c) => c.id).some((c) => Object.keys(sortColumns).includes(c))) {
      const columnDef = columns.filter((c) => Object.keys(sortColumns).includes(c.id))[0];
      result = result.sort(columnDef.sortFunction);
      if (sortColumns[columnDef.id].direction !== 'asc') result = result.reverse();
    }
    return result;
  }, [data, sortColumns]);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    handleChangePage(event, 0);
  };

  const isSelectAllChecked = () => {
    function isSuperset(setA, setB) {
      return setB.every((elem) => setA.includes(elem));
    }
    const curPageItems = data
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      .map((r) => r[rowIdKey]);
    return selected && isSuperset(selected, curPageItems);
  };

  const handleSelectAllClick = (event) => {
    const curPageItems = data
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      .map((r) => r[rowIdKey]);
    if (event.target.checked) {
      // Union all new members from this page onto selection set.
      onItemSelected([...new Set([...selected, ...curPageItems])]);
      return;
    }
    // Remove all of this page's items from currently selected.
    onItemSelected([...selected.filter((item) => !curPageItems.includes(item))]);
  };

  const handleSort = (column) => () => {
    let newSortColumns = { ...sortColumns }; // Create a shallow copy for react to detect changes.
    if (column.id in sortColumns) {
      // Reverse
      newSortColumns[column.id].direction =
        newSortColumns[column.id].direction === 'asc' ? 'desc' : 'asc';
      setSortColumns(newSortColumns);
      return;
    }
    // Delete this code when enabling multi-column sorting
    newSortColumns = {};
    newSortColumns[column.id] = { direction: 'asc', order: 1 };
    setSortColumns(newSortColumns);
  };

  const handleItemSelected = (event, row) => {
    const rowId = row[rowIdKey];
    if (!event.target.checked) {
      // This is prevstate
      onItemSelected(selected.filter((id) => id !== rowId));
      return;
    }
    // Add to selected
    onItemSelected([...selected, rowId]);
  };

  const isSelected = (row) => selected.includes(row[rowIdKey]);

  return (
    <Page>
      <Row>
        <TableContainer style={{ width: '100%' }}>
          <Table stickyHeader aria-label="points table" size="small">
            <TableHead>
              <TableRow>
                <TableCell colSpan={3} sx={{ borderBottom: 'none' }}>
                  <Box display="flex" alignItems="center" mb={2} mt={2}>
                    <Typography variant="h5" component="h2">
                      {title}
                    </Typography>
                    <Typography variant="body1" color="textSecondary" style={{ marginLeft: '8px' }}>
                      {selected.length ? `(${selected.length}/${rowCount})` : `(${rowCount})`}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell colSpan={columns.length - 2} align="right" sx={{ borderBottom: 'none' }}>
                  <Box display="flex" justifyContent="flex-end" gap={2} alignItems="center">
                    {children}
                    <MultiEditMenu>
                      {actions.map((a) => (
                        <MenuItem
                          key={a.id}
                          disabled={selected.length <= 0}
                          disableRipple
                          onClick={() =>
                            a.doOpen({
                              column: columns.find((c) => c.action === a.id),
                              setCurrentlyEditing,
                              currentEditor,
                              setCurrentEditor,
                              editRows: selected.map((s) => data.find((d) => d[rowIdKey] === s)),
                            })
                          }
                        >
                          {a.label}
                        </MenuItem>
                      ))}
                    </MultiEditMenu>
                  </Box>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Tooltip title="Select All">
                    <Checkbox
                      color="success"
                      indeterminate={numSelected > 0 && numSelected < rowCount}
                      checked={isSelectAllChecked()}
                      onChange={handleSelectAllClick}
                      inputProps={{
                        'aria-label': 'select all rows on this page',
                      }}
                    />
                  </Tooltip>
                </TableCell>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}
                    sortDirection={
                      column.id in sortColumns ? sortColumns[column.id].direction : false
                    }
                  >
                    {column.sortable ? (
                      <TableSortLabel
                        active={column.id in sortColumns}
                        direction={sortColumns[column.id]?.direction || 'asc'}
                        onClick={handleSort(column)}
                      >
                        <Typography>
                          <b>{column.label}</b>
                          {column.id in sortColumns ? (
                            <Box
                              component="span"
                              style={{ verticalAlign: 'super', fontSize: '0.7em' }}
                            >
                              {sortColumns[column.id].order}
                            </Box>
                          ) : null}
                        </Typography>
                      </TableSortLabel>
                    ) : (
                      <b>{column.label}</b>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow
                      sx={{
                        transition: 'box-shadow 0.1s ease-in-out', // Smooth transition for the shadow
                        '&:hover': {
                          boxShadow: '0px 0px 5px rgba(0, 0, 0, 0.3)',
                        },
                      }}
                      role="checkbox"
                      tabIndex={-1}
                      key={row[rowIdKey]}
                    >
                      <TableCell padding="checkbox">
                        <Tooltip title="Select">
                          <Checkbox
                            color="success"
                            checked={isSelected(row)}
                            onChange={(e) => handleItemSelected(e, row)}
                            inputProps={{
                              'aria-labelledby': labelId,
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                      {columns.map((column) => {
                        switch (column.type) {
                          case 'badges':
                            return <BadgesDataCell column={column} value={row} key={column.id} />;
                          case 'identity':
                            return <IdentityDataCell column={column} value={row} key={column.id} />;
                          case 'linear':
                            return <LinearDataCell column={column} value={row} key={column.id} />;
                          case 'button':
                            return (
                              <ButtonDataCell
                                column={column}
                                value={row}
                                key={column.id}
                                valueId={row[rowIdKey]}
                                onClick={() => {
                                  const action = actions.find((a) => a.id === column.action);
                                  action.doOpen({
                                    column,
                                    currentEditor,
                                    setCurrentEditor,
                                    setCurrentlyEditing,
                                    editRows: [row],
                                  });
                                }}
                              />
                            );
                          default:
                            return <TextDataCell column={column} value={row} key={column.id} />;
                        }
                      })}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Row>
      <Row>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Row>
      {renderEditorDialog()}
    </Page>
  );
};

const mapStateToProps = (state) => {
  /* It's gross that we're keeping the page in redux, but this is the path of least resistance when we push pagination logic to the queries
   * If we want to revert this, look at the commit for this comment as this change was done as one atomic unit */
  const {
    entities: {
      staging: { page },
    },
  } = state;
  return { page };
};

const mapDispatchToProps = (dispatch) => ({
  handleChangePage: (event, newPage) => dispatch({ type: SET_PAGE, newPage }),
});

export default connect(mapStateToProps, mapDispatchToProps)(StagingDataTable);
